import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Buildings, Plus, PencilSimple, TrashSimple, Lock, Tag } from "@phosphor-icons/react";

export default function ProjectTypes() {
  const { user } = useAuth();
  const askConfirm = useConfirm();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: "", subtypes: [] });
  const [newSub, setNewSub] = useState("");
  const isAdmin = user?.role === "yonetici";

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/project-types`);
      setTypes(r.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ label: "", subtypes: [] });
    setNewSub("");
    setOpenDialog(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ label: t.label, subtypes: [...(t.subtypes || [])] });
    setNewSub("");
    setOpenDialog(true);
  };
  const closeDialog = () => { setOpenDialog(false); setEditing(null); };

  const addSub = () => {
    const val = newSub.trim();
    if (!val) return;
    if (form.subtypes.find(s => s.label.toLowerCase() === val.toLowerCase())) {
      toast.error("Bu alt tip zaten var");
      return;
    }
    setForm(f => ({ ...f, subtypes: [...f.subtypes, { key: "", label: val }] }));
    setNewSub("");
  };
  const removeSub = (idx) => {
    setForm(f => ({ ...f, subtypes: f.subtypes.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!form.label.trim()) { toast.error("Tip adı zorunlu"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/project-types/${editing.key}`, {
          label: form.label.trim(),
          subtypes: form.subtypes,
        });
        toast.success("Proje tipi güncellendi");
      } else {
        await axios.post(`${API}/project-types`, {
          label: form.label.trim(),
          subtypes: form.subtypes,
        });
        toast.success("Proje tipi eklendi");
      }
      closeDialog();
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (t) => {
    if (t.is_system) { toast.error("Sistem tipi silinemez"); return; }
    const ok = await askConfirm({
      title: "Proje Tipini Sil",
      description: `"${t.label}" tipini silmek istiyor musunuz? Bu tipte proje varsa işlem başarısız olur.`,
      destructive: true, confirmText: "Sil",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/project-types/${t.key}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Shell
      title="Proje Tipleri"
      subtitle="Ayarlar"
      hideSiteSelector
      actions={isAdmin && (
        <Button onClick={openNew} data-testid="pt-add"
                className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white text-xs uppercase tracking-widest font-bold">
          <Plus size={14} className="mr-1.5" /> Yeni Tip
        </Button>
      )}
    >
      <div className="flex items-center gap-2 mb-6">
        <Buildings size={20} weight="duotone" className="text-[#95C11F]" />
        <p className="text-sm text-slate-600">
          Proje ana tipleri (Alt Yapı, Üst Yapı vb.) ve her tipe ait alt kırılımları burada yönetin.
          Sistem tipleri silinemez ama alt tipleri özelleştirilebilir.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-8 text-sm text-slate-500">Yükleniyor...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {types.map(t => (
            <Card key={t.key} data-testid={`pt-card-${t.key}`}
                  className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  {t.is_system && (
                    <div className="mb-1">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 text-slate-600 font-bold text-[10px] uppercase tracking-widest">
                        <Lock size={9} weight="fill" /> Sistem
                      </span>
                    </div>
                  )}
                  <div className="text-lg font-bold text-slate-900 font-heading">{t.label}</div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 font-mono mt-1">
                    {t.key}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(t)}
                            data-testid={`pt-edit-${t.key}`}
                            className="w-8 h-8 flex items-center justify-center text-[#1B4776] hover:bg-slate-100"
                            title="Düzenle">
                      <PencilSimple size={14} />
                    </button>
                    {!t.is_system && (
                      <button onClick={() => del(t)}
                              data-testid={`pt-del-${t.key}`}
                              className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50"
                              title="Sil">
                        <TrashSimple size={14} />
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="pt-3 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                  Alt Tipler ({t.subtypes?.length || 0})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(t.subtypes || []).map(st => (
                    <span key={st.key}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-[#F7FBE9] text-[#1B4776] text-xs font-medium border border-[#95C11F]/40">
                      <Tag size={10} weight="fill" className="text-[#95C11F]" /> {st.label}
                    </span>
                  ))}
                  {(!t.subtypes || t.subtypes.length === 0) && (
                    <span className="text-xs text-slate-400 italic">Alt tip tanımlanmamış</span>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={openDialog} onOpenChange={(v) => !v && closeDialog()}>
        <DialogContent className="rounded-none sm:rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight font-heading">
              {editing ? `Proje Tipini Düzenle: ${editing.label}` : "Yeni Proje Tipi"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              Ana tip adı ve alt kırılımları girin. Örn: "Alt Yapı" → yol, su, kanalizasyon.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Tip Adı</Label>
              <Input value={form.label}
                     data-testid="pt-form-label"
                     onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                     placeholder="Örn: Endüstriyel Yapı"
                     className="mt-2 rounded-none sm:rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Alt Tipler</Label>
              <div className="mt-2 flex gap-2">
                <Input value={newSub}
                       data-testid="pt-form-newsub"
                       onChange={e => setNewSub(e.target.value)}
                       onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addSub(); } }}
                       placeholder="Alt tip adı yazın, Ekle'ye basın"
                       className="rounded-none sm:rounded-sm" />
                <Button type="button" onClick={addSub}
                        data-testid="pt-form-addsub"
                        className="rounded-none sm:rounded-sm bg-[#95C11F] hover:bg-[#7ea11a] text-[#1B2A3A] font-bold">
                  <Plus size={14} />
                </Button>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 min-h-[32px]">
                {form.subtypes.map((st, i) => (
                  <span key={i}
                        className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 bg-[#F7FBE9] text-[#1B4776] text-xs font-medium border border-[#95C11F]/40">
                    {st.label}
                    <button onClick={() => removeSub(i)}
                            data-testid={`pt-form-delsub-${i}`}
                            className="w-4 h-4 flex items-center justify-center text-red-600 hover:bg-red-100">
                      ×
                    </button>
                  </span>
                ))}
                {form.subtypes.length === 0 && (
                  <span className="text-xs text-slate-400 italic">Henüz alt tip eklenmedi</span>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}
                    className="rounded-none sm:rounded-sm">İptal</Button>
            <Button onClick={save} data-testid="pt-form-save"
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              {editing ? "Güncelle" : "Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
